import { useEffect, useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useBranding } from '@/lib/branding'
import { docsBySlug, guideDocs, exampleDocs } from '../manifest'
import type { DocEntry } from '../types'
import { MarkdownRenderer } from './MarkdownRenderer'
import { DocBreadcrumbs } from './DocBreadcrumbs'
import { DocToc } from './DocToc'
import { DocPager } from './DocPager'
import { DocNotFound } from './DocNotFound'
import { extractTocItems } from '../tocUtils'

/** Flat ordered list used for prev/next navigation — guides first, examples after. */
const allDocs: DocEntry[] = [...guideDocs, ...exampleDocs]

/** Find the index of a doc in the ordered list by slug. */
function findDocIndex(slug: string): number {
  return allDocs.findIndex((d) => d.slug === slug)
}

/** Scroll to the hash anchor after content mounts / hash changes. */
function useScrollToAnchor(hash: string, slug: string | undefined) {
  useEffect(() => {
    if (!hash) return
    const id = hash.slice(1)
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [hash, slug])
}

/** Update document.title for the current doc and restore it on unmount. */
function useDocTitle(title: string | undefined, portalName: string) {
  useEffect(() => {
    if (!title) return
    const previous = document.title
    document.title = `${title} — ${portalName} Developer Portal`
    return () => {
      document.title = previous
    }
  }, [title, portalName])
}

/**
 * Move keyboard focus to the #main-content landmark on SPA navigations so
 * screen reader users hear the new page heading (WCAG 2.4.3).
 * Keyed on slug — fires whenever the slug changes.
 */
function useFocusMain(slug: string | undefined) {
  useEffect(() => {
    document.getElementById('main-content')?.focus({ preventScroll: true })
  }, [slug])
}

interface DocPageProps {
  doc: DocEntry
  prev: DocEntry | undefined
  next: DocEntry | undefined
  breadcrumbs: Array<{ label: string; to?: string }>
}

/**
 * Shared inner doc page renderer — handles TOC, breadcrumbs, pager,
 * and document title. Extracted so OnboardingDoc and OnboardingExample
 * share the same chrome.
 */
function DocPage({ doc, prev, next, breadcrumbs }: DocPageProps) {
  const tocItems = useMemo(() => extractTocItems(doc.body), [doc.body])

  return (
    <div className="flex gap-8 lg:gap-12">
      {/* Main content column */}
      <div className="min-w-0 flex-1">
        <DocBreadcrumbs crumbs={breadcrumbs} />
        <MarkdownRenderer body={doc.body} />
        <DocPager prev={prev} next={next} />
      </div>

      {/* In-page TOC — xl viewport only */}
      <DocToc items={tocItems} />
    </div>
  )
}

/**
 * Renders a guide doc identified by :slug (e.g. "01-tenant-setup").
 */
export function OnboardingDoc() {
  // TRUST_BOUNDARY: :slug from URL path param, validated by docsBySlug.get()
  // (returns undefined for unknown slugs) and the doc.isExample guard below.
  const { slug } = useParams<{ slug: string }>()
  const { hash } = useLocation()
  const { display_name } = useBranding()

  const doc = slug ? docsBySlug.get(slug) : undefined

  // W1: reject example docs accessed via the guide route — treat as not-found.
  const guideDoc = doc && !doc.isExample ? doc : undefined

  const idx = slug ? findDocIndex(slug) : -1
  const prev = idx > 0 ? allDocs[idx - 1] : undefined
  const next = idx >= 0 && idx < allDocs.length - 1 ? allDocs[idx + 1] : undefined

  useScrollToAnchor(hash, slug)
  useDocTitle(guideDoc?.title, display_name || 'Antifraud')
  useFocusMain(slug)

  if (!slug || !guideDoc) {
    return <DocNotFound slug={slug ?? ''} />
  }

  const breadcrumbs = [
    { label: 'Docs', to: '/developers/onboarding' },
    { label: 'Guide', to: '/developers/onboarding' },
    { label: guideDoc.title },
  ]

  return (
    <DocPage
      doc={guideDoc}
      prev={prev}
      next={next}
      breadcrumbs={breadcrumbs}
    />
  )
}

/**
 * Renders an example doc identified by :slug (e.g. "curl").
 */
export function OnboardingExample() {
  // TRUST_BOUNDARY: :slug from URL path param, validated by docsBySlug.get()
  // (returns undefined for unknown slugs) and the !doc.isExample guard below.
  const { slug } = useParams<{ slug: string }>()
  const { hash } = useLocation()
  const { display_name } = useBranding()

  const doc = slug ? docsBySlug.get(slug) : undefined

  // W1: reject guide docs accessed via the examples route — treat as not-found.
  const exampleDoc = doc?.isExample ? doc : undefined

  const idx = slug ? findDocIndex(slug) : -1
  const prev = idx > 0 ? allDocs[idx - 1] : undefined
  const next = idx >= 0 && idx < allDocs.length - 1 ? allDocs[idx + 1] : undefined

  useScrollToAnchor(hash, slug)
  useDocTitle(exampleDoc?.title, display_name || 'Antifraud')
  useFocusMain(slug)

  if (!slug || !exampleDoc) {
    return <DocNotFound slug={slug ?? ''} />
  }

  const breadcrumbs = [
    { label: 'Docs', to: '/developers/onboarding' },
    { label: 'Examples' },
    { label: exampleDoc.title },
  ]

  return (
    <DocPage
      doc={exampleDoc}
      prev={prev}
      next={next}
      breadcrumbs={breadcrumbs}
    />
  )
}

/**
 * Home page — renders README.md (the overview).
 */
export function OnboardingHome() {
  const { hash } = useLocation()
  const { display_name } = useBranding()

  const doc = docsBySlug.get('readme')
  const idx = findDocIndex('readme')
  const next = idx >= 0 && idx < allDocs.length - 1 ? allDocs[idx + 1] : undefined

  useScrollToAnchor(hash, 'readme')
  useDocTitle(doc?.title, display_name || 'Antifraud')

  if (!doc) {
    return (
      <div
        role="status"
        className="rounded-md border border-border bg-muted/40 p-6 text-sm text-muted-fg"
      >
        Documentation not found.
      </div>
    )
  }

  const tocItems = extractTocItems(doc.body)

  return (
    <div className="flex gap-8 lg:gap-12">
      <div className="min-w-0 flex-1">
        <MarkdownRenderer body={doc.body} />
        <DocPager prev={undefined} next={next} />
      </div>
      <DocToc items={tocItems} />
    </div>
  )
}
